import React, { ReactNode } from "react";
import styled, { css } from "styled-components";
import { ChevronRight } from "lucide-react";
import RouterLink from "common/Buttons/RouterLink";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookHero,
  newLookType,
} from "common/NewLook/tokens.ts";

interface PanelProps {
  title?: ReactNode;
  /** Shown next to the title in a quiet count badge. */
  count?: number;
  /** Next to the title, e.g. a hot-trades note. */
  extra?: ReactNode;
  /** Right side of the header, e.g. a sort control or tabs. */
  actions?: ReactNode;
  /** Route for the header's "See all" link. */
  seeAllTo?: string;
  seeAllLabel?: string;
  children: ReactNode;
  className?: string;
  /** Drops the body padding, for edge-to-edge rows. */
  flush?: boolean;
  /** The page's one hero surface (soft brand tint). */
  hero?: boolean;
  "data-testid"?: string;
}

/**
 * A dashboard panel: one white surface on the tinted canvas, with its title
 * inside it (so a section reads as one object), a soft shadow (light)
 * or a hairline (dark).
 */
const Panel = ({
  title,
  count,
  extra,
  actions,
  seeAllTo,
  seeAllLabel,
  children,
  className,
  flush,
  hero,
  "data-testid": testId,
}: PanelProps) => (
  <Surface className={className} data-testid={testId} $hero={hero}>
    {(title || actions || seeAllTo) && (
      <Header>
        <TitleGroup>
          {title && <Title>{title}</Title>}
          {count !== undefined && <Count>{count}</Count>}
          {extra}
        </TitleGroup>
        <Actions>
          {actions}
          {seeAllTo && (
            <RouterLink to={seeAllTo}>
              <SeeAll>
                {seeAllLabel}
                <ChevronRight size={14} strokeWidth={2} />
              </SeeAll>
            </RouterLink>
          )}
        </Actions>
      </Header>
    )}
    <Body $flush={flush}>{children}</Body>
  </Surface>
);

export default Panel;

const Surface = styled.section<{ $hero?: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border: ${(p) => p.theme.borders.panel};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  box-shadow: ${(p) => p.theme.shadows.panel};
  ${(p) => p.$hero && newLookHero};
`;
const Header = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  min-height: 60px;
  padding: 12px 20px 12px 24px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const TitleGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;
const Title = styled.h2`
  margin: 0;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const Count = styled.span`
  ${newLookType.meta};
  font-weight: 500;
  min-width: 20px;
  padding: 1px 6px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  text-align: center;
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
  margin-left: auto;
`;
const SeeAll = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 28px;
  padding: 0 4px 0 8px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).accent};
  white-space: nowrap;
  transition: background-color 120ms;
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
`;
const Body = styled.div<{ $flush?: boolean }>`
  position: relative;
  flex: 1;
  min-width: 0;
  ${(p) =>
    !p.$flush &&
    css`
      padding: 20px 24px 24px;
    `}
`;
